import {
  Component,
  HostBinding,
  Input,
  OnInit,
  Renderer2,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import {
  HeightInAnimation,
  HeightOutAnimation,
  OpacityInAnimation,
  OpacityOutAnimation,
} from '../../animations/index';
import { BehaviorSubject } from 'rxjs';
import { MatRipple } from '@angular/material/core';
import { DOCUMENT, NgClass, AsyncPipe } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { CdkAriaLive } from '@angular/cdk/a11y';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'mulo-copier',
    templateUrl: './copier.component.html',
    styleUrls: ['./copier.component.scss'],
    animations: [
        HeightInAnimation,
        HeightOutAnimation,
        OpacityInAnimation,
        OpacityOutAnimation,
    ],
    imports: [NgClass, MatRipple, MatButton, MatIcon, CdkAriaLive, AsyncPipe]
})
export class CopierComponent implements OnInit {
  private document = inject(DOCUMENT);
  private renderer = inject(Renderer2);
  private cb = inject(Clipboard);
  private _window: any = this.document.defaultView;

  readonly copyLabel = input('Copy');
  readonly copiedLabel = input('Copied!');
  readonly copyIcon = input('copy');
  readonly rtlMirrorIcon = input<boolean>(undefined);
  readonly processing = input(false);
  readonly display = input<'single' | 'wrapping'>('single');
  readonly copyMethod = input<'rich' | 'plain'>('plain');
  readonly content = input<any>('ksajdljasdljasldkjasd');
  // TODO: Skipped for migration because:
  //  This input is used in combination with `@HostBinding` and migrating would
  //  break.
  @HostBinding('style.--button-indent') @Input() btnIndent: string;
  readonly copied = output<null>();
  readonly ripple = viewChild(MatRipple);

  copying$ = new BehaviorSubject<boolean>(false);
  private copyListener: () => void;

  ngOnInit(): void {}

  copyToClipboard(ev: MouseEvent) {
    ev.stopImmediatePropagation();
    this.ripple().disabled = true;
    this.copying$.next(true);
    this.launchRipple(ev.screenX, ev.screenY);
    this.copyByMethod();
  }

  copyByMethod() {
    const htmlContent = this.content();
    const textContent = new DOMParser()
      .parseFromString(htmlContent, 'text/html')
      .body.textContent.trim();

    if (this.copyMethod() === 'plain') {
      // When using preformatted (<pre>) content, use CDK clipboards
      this.cb.copy(this.content());
    } else if (this._window?.navigator?.clipboard?.write) {
      // ...otherwise try to use the async clipboard api if available...
      const data = [
        new this._window.ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([textContent], { type: 'text/plain' }),
        }),
      ];
      this._window.navigator.clipboard.write(data);
    } else {
      // ...otherwise use an event listener on the document
      const listener = (ev) => {
        ev.preventDefault();
        ev.clipboardData.setData('text/html', htmlContent);
        ev.clipboardData.setData('text/plain', textContent);
      };
      this.copyListener = this.renderer.listen('document', 'copy', listener);
      this.document.execCommand('copy');
    }
    setTimeout(() => {
      this.copied.emit(null);
    });
  }

  launchRipple(x: number, y: number) {
    const rippleRef = this.ripple().launch(x, y, {
      persistent: true,
      centered: false,
      animation: { enterDuration: 300, exitDuration: 500 },
    });

    const unlisteners = [];
    for (const ev of ['click', 'keydown.enter', 'keydown.space']) {
      unlisteners.push(
        this.renderer.listen('window', ev, () => {
          // Fade out the ripple later.
          rippleRef.fadeOut();
          // remove copy event listener if used
          if (this.copyListener) this.copyListener();

          setTimeout(() => {
            // Emit end of copy process
            this.copying$.next(false);
            // discard listeners nicely
            unlisteners.forEach((ul) => ul());
          }, 500);
        }),
      );
    }
  }
}
